import React, { useEffect, useState } from "react";
import {
  Modal,
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CustomSelect } from "../../CustomControls/CustomSelect";
import { useClinicalTrialContext } from "../../../containers/ClinicalTrials/ClinicalTrialContext";
import { Spinner } from "../../Utils/Loaders";
import { getCreateRegulatoryEvents } from "../../../api/clinicalTrial";
import {
  attachmentEntityType,
  regulatorySubmissionTypes,
} from "../../../variables/Enums";
import { CustomRadio } from "../../CustomControls/CustomRadio";

export const ModalCreateRegulatoryEvent = ({ show, onClose, onSave }) => {
  const { t } = useTranslation();
  const { startup } = useClinicalTrialContext();
  const [formData, setFormData] = useState({
    title: "",
    type: String(regulatorySubmissionTypes.notification),
    categories: [],
    groups: [],
    patients: [],
    protocolAttachments: [],
  });

  const [typesOptions, setTypesOptions] = useState();
  const [categoryOptions, setCategoryOptions] = useState();
  const [patientOptions, setPatientOptions] = useState();
  const [groupOptions, setGroupOptions] = useState();
  const [protocolAttachmentOptions, setProtocolAttachmentOptions] = useState();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    var loadData = async () => {
      const result = await getCreateRegulatoryEvents(startup.protocol.id);

      if (result.status === 200) {
        const types = result.data.regulatorySubmissionTypes;
        const categories = result.data.attachmentCategories;

        setTypesOptions(types);

        const groupedCategories = [
          {
            label: t("regulatorySubmissions.createEditModal.startupCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Startup)
              .map((x) => ({ id: x.id, name: x.name })),
          },
          {
            label: t(
              "regulatorySubmissions.createEditModal.practitionersCategory"
            ),
            options: categories
              .filter(
                (x) => x.entityTypeId === attachmentEntityType.Practitioner
              )
              .map((x) => ({
                id: x.id,
                name: `${x.practitionerName ? x.practitionerName + " - " : ""}${
                  x.name
                }`,
                practitionerId: x.practitionerId,
              })),
          },
          {
            label: t("regulatorySubmissions.createEditModal.hospitalCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Hospital)
              .map((x) => ({ id: x.id, name: x.name })),
          },
          {
            label: t("regulatorySubmissions.createEditModal.protocolCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Protocols)
              .map((x) => ({
                id: x.id,
                name: `${x.medicalRecordNumber}${x.name}`,
                medicalRecordNumber: x.medicalRecordNumber,
              })),
          },
          {
            label: t(
              "regulatorySubmissions.createEditModal.regulatorySubmissionCategory"
            ),
            options: categories
              .filter(
                (x) =>
                  x.entityTypeId === attachmentEntityType.RegulatorySubmissions
              )
              .map((x) => ({
                id: x.id,
                name: x.name,
              })),
          },
        ];

        const groups = result.data.groups.map((x) => ({
          id: x.id,
          name: x.name,
        }));

        const patients = result.data.patients.map((x) => ({
          id: x.medicalRecordNumber,
          name: x.numberInProtocol || x.medicalRecordNumber,
          numberInProtocol: x.numberInProtocol || null,
        }));

        const protocolAttachments = result.data.protocolAttachments.map(
          (x) => ({
            id: x.id,
            name: x.name,
            medicalRecordNumber: x.medicalRecordNumber,
          })
        );

        const withoutPatient = protocolAttachments
          .filter((x) => !x.medicalRecordNumber)
          .map((x) => ({ id: x.id, name: x.name }));

        const attachmentsWithPatient = protocolAttachments.filter(
          (x) => x.medicalRecordNumber
        );

        const groupedByPatient = attachmentsWithPatient.reduce((acc, item) => {
          if (!acc[item.medicalRecordNumber]) {
            acc[item.medicalRecordNumber] = [];
          }
          acc[item.medicalRecordNumber].push({ id: item.id, name: item.id });
          return acc;
        }, {});

        const groupedProtocolAttachments = [
          {
            label: t(
              "regulatorySubmissions.createEditModal.protocolAttachments"
            ),
            options: withoutPatient,
          },
          ...Object.entries(groupedByPatient).map(
            ([medicalRecordNumber, options]) => ({
              label: medicalRecordNumber,
              options,
            })
          ),
        ];

        setCategoryOptions(groupedCategories);
        setGroupOptions(groups);
        setPatientOptions(patients);
        setProtocolAttachmentOptions(groupedProtocolAttachments);
      }
    };

    if (!startup) {
      return;
    }

    loadData();
    setFormData({
      type: String(regulatorySubmissionTypes.notification),
      title: "",
      categories: [],
      groups: [],
      patients: [],
      protocolAttachments: [],
    });
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const { type, title, categories, groups, protocolAttachments, patients } =
      formData;

    const patientSelections = patients || [];

    const payload = {
      name: title,
      regulatorySubmissionTypeId: type,
      groups: groups.map((g) => g.id),
      protocolAttachments: protocolAttachments.map((a) => ({
        id: a.id,
        medicalRecordNumber: a.medicalRecordNumber,
      })),
      attachmentsCategories: categories.map((cat) => ({
        id: cat.id,
        practitionerId: cat.practitionerId || null,
      })),
      patients: patientSelections.map((p) => ({
        medicalRecordNumber: p.id,
        numberInProtocol: p.numberInProtocol ?? null,
      })),
      medicalRecordNumber: patientSelections[0]?.id ?? null,
      numberInProtocol: patientSelections[0]?.numberInProtocol ?? null,
    };

    setSaving(true);

    onSave(payload, setSaving);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      dialogClassName="rounded-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("regulatorySubmissions.createEditModal.addRegulatory")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(!categoryOptions || saving) && <Spinner />}
        {categoryOptions && !saving && (
          <>
            <FormGroup
              controlId="formTitle"
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ControlLabel>
                {t("regulatorySubmissions.type")}{" "}
                <span className="text-danger">*</span>
              </ControlLabel>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {typesOptions.map((opt) => (
                  <div
                    key={opt}
                    style={{ marginLeft: opt === 2 ? "10px" : "0" }}
                  >
                    <CustomRadio
                      number={`type-${opt}`}
                      name="type"
                      label={t(`regulatorySubmissions.types.${opt}`)}
                      option={opt}
                      checked={formData.type === String(opt)}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </div>
            </FormGroup>

            <FormGroup controlId="formTitle">
              <ControlLabel>{t("regulatorySubmissions.title")}</ControlLabel>
              <FormControl
                type="text"
                placeholder="Título"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                readOnly={saving}
              />
            </FormGroup>
            <FormGroup controlId="formPatients">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.patientsSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.patients || []}
                name="patients"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                options={patientOptions || []}
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
            <FormGroup controlId="formGroups">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.groupsSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.groups || ""}
                name="groups"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                options={groupOptions || []}
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
            <FormGroup controlId="formCategories">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.categoriesSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.categories || ""}
                name="categories"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                options={categoryOptions || []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
            <FormGroup controlId="formProtocolAttachments">
              <ControlLabel>
                {t(
                  "regulatorySubmissions.createEditModal.protocolAttachmentsSelect"
                )}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.protocolAttachments}
                name="protocolAttachments"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                options={protocolAttachmentOptions || []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          {t("regulatorySubmissions.createEditModal.cancelLabel")}
        </Button>
        {!saving && (
          <Button
            disabled={!formData.type}
            variant="btn btn-primary"
            onClick={handleSubmit}
          >
            {t("regulatorySubmissions.createEditModal.acceptLabel")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
